// Centrální wrapper pro volání backendu. NIKDY používat holý fetch().

export class ApiError extends Error {
  status: number;
  details?: unknown;
  constructor(status: number, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.details = details;
  }

  // Vrátí human-readable popis chyby + případně field-level chyby ze Zodu.
  describe(): string {
    const d = this.details as { fieldErrors?: Record<string, string[] | undefined> } | undefined;
    const fields = d?.fieldErrors;
    if (fields) {
      const lines = Object.entries(fields)
        .map(([k, v]) => (v && v.length ? `${k}: ${v.join(', ')}` : null))
        .filter((x): x is string => x !== null);
      if (lines.length) return `${this.message} — ${lines.join('; ')}`;
    }
    return this.message;
  }
}

type ApiOptions = Omit<RequestInit, 'body'> & {
  /** Objekt se pošle jako JSON; Blob/ArrayBuffer se pošle tak, jak je. */
  body?: unknown;
};

/** Tělo, které se neserializuje do JSON (soubory, PDF). */
function jeBinarni(body: unknown): body is BodyInit {
  return (
    body instanceof Blob ||
    body instanceof ArrayBuffer ||
    body instanceof Uint8Array ||
    body instanceof FormData
  );
}

export async function apiFetch<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const headers = new Headers(opts.headers);
  const binarni = jeBinarni(opts.body);
  if (opts.body !== undefined && !binarni && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(path, {
    ...opts,
    headers,
    credentials: 'include',
    body: binarni
      ? (opts.body as BodyInit)
      : opts.body !== undefined
        ? JSON.stringify(opts.body)
        : undefined,
  });

  const text = await res.text();
  const json: unknown = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const j = json as { error?: string; details?: unknown } | null;
    throw new ApiError(res.status, j?.error ?? `HTTP ${res.status}`, j?.details);
  }

  return json as T;
}
